import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Book, Globe, MessageSquare, Landmark, Paperclip } from 'lucide-react';
import { GospelLensIcon } from '@/components/GospelLensIcon';
import { extractFollowupChips } from '@/utils/extractFollowupChips';

export function MessageDisplay({ content, onScriptureClick }) {
  const { t } = useTranslation();
  // Strip the FOLLOWUP_CHIPS marker before any other parsing — chips are
  // rendered separately by ChatMessageBubble; here we only care about the
  // visible prose.
  const { content: cleanContent } = extractFollowupChips(content);
  // Define separators
  // Each section can be matched against multiple header *labels* that the
  // model might emit. We tolerate:
  //   • "### Scripture References"          (canonical)
  //   • "## Scripture References"
  //   • "Scripture References"              (after server bold-strip, no markdown)
  //   • "Scripture References:"             (with trailing colon)
  //   • case-insensitive matches
  //   • optional trailing whitespace
  // Match must be on its own line so phrases mid-paragraph don't match.
  const sectionsConfig = [
    {
      id: 'scripture',
      header: '### Scripture References',
      labels: ['Scripture References'],
      icon: <Book className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />,
      translationKey: 'chat.sections.scripture',
      renderAsChips: true,
    },
    {
      id: 'language',
      header: '### Original Language Analysis',
      labels: ['Original Language Analysis', 'Greek/Hebrew Analysis', 'Greek Analysis', 'Hebrew Analysis'],
      icon: <Globe className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />,
      translationKey: 'chat.sections.language'
    },
    {
      id: 'commentator',
      header: '### Commentator Insights',
      labels: ['Commentator Insights', 'Commentator Insight', 'Commentary Insights'],
      icon: <MessageSquare className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />,
      translationKey: 'chat.sections.commentator'
    },
    {
      id: 'christ_connection',
      header: '### How this points to Christ',
      labels: ['How this points to Christ', 'How This Points to Christ', 'Pointing to Christ'],
      icon: <GospelLensIcon className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />,
      translationKey: 'chat.sections.christ_connection'
    },
    {
      id: 'scriptural_background',
      header: '### Scriptural Background',
      labels: ['Scriptural Background'],
      icon: <Book className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />,
      translationKey: 'chat.sections.scriptural_background'
    },
    {
      id: 'practical_application',
      header: '### Practical Application',
      labels: ['Practical Application', 'Application'],
      icon: <MessageSquare className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />,
      translationKey: 'chat.sections.practical_application'
    },
    {
      id: 'historical_context_cultural',
      header: '### Historical & Cultural Context',
      labels: ['Historical & Cultural Context', 'Historical and Cultural Context', 'Cultural & Historical Context'],
      icon: <Landmark className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />,
      translationKey: 'chat.sections.historical_cultural_context',
      fallbackLabel: 'Historical & Cultural Context'
    },
    {
      id: 'historical_context',
      header: '### Historical Context',
      labels: ['Historical Context', 'Cultural Context'],
      icon: <Landmark className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />,
      translationKey: 'chat.sections.historical_context',
      fallbackLabel: 'Historical Context'
    }
  ];

  // 1. Extract Image
  const imageRegex = /!\[(.*?)\]\((.*?)\)/;
  
  const fullImageMatch = cleanContent.match(imageRegex);
  let fullMainText = cleanContent;
  if (fullImageMatch) {
      fullMainText = cleanContent.replace(fullImageMatch[0], '').trim();
  }

  // ── Tolerant section matching ──────────────────────────────────────
  // Build a single regex that finds ANY recognized section header
  // anywhere on its own line, regardless of markdown decoration. This
  // catches the common bug where the server's bold-strip turns
  // `**Scripture References**` (intended appendix header) into plain
  // `Scripture References` — without this regex, that line would be
  // misclassified as body content and bleed into the main response.
  //
  // For each match we record:
  //   - lineStart   : index of the line containing the header (where
  //                   the header line begins, used to slice the body
  //                   BEFORE this section).
  //   - bodyStart   : index right after the header line (where the
  //                   section's body begins).
  //   - config      : which sectionsConfig entry it belongs to.
  //
  // We then sort by lineStart, slice body[fullMainText..lineStart] as
  // the visible main response, and slice each section as
  // body[bodyStart..nextLineStart].
  const labelToConfig = new Map();
  const labelPatterns = [];
  for (const cfg of sectionsConfig) {
    for (const label of cfg.labels) {
      labelToConfig.set(label.toLowerCase(), cfg);
      // Escape regex special chars in the label, then build a pattern
      // matching: optional markdown prefix (###/##/#), the label, optional
      // colon, optional trailing whitespace — all on its own line.
      const escaped = label.replace(/[.*+?^${}()|[\]\\&]/g, '\\$&');
      labelPatterns.push(`(?:#{1,6}\\s+)?${escaped}\\s*:?`);
    }
  }
  // Combined regex with line anchors. The `m` flag makes ^/$ match per
  // line; `i` is case-insensitive so "Scripture references" matches.
  const headerRegex = new RegExp(
    `^[ \\t]*(${labelPatterns.join('|')})[ \\t]*$`,
    'gim'
  );

  let matches = [];
  let m;
  while ((m = headerRegex.exec(fullMainText)) !== null) {
    const headerLine = m[1];
    // Strip markdown prefix + trailing colon to recover the canonical label
    const labelKey = headerLine
      .replace(/^#{1,6}\s+/, '')
      .replace(/\s*:?\s*$/, '')
      .trim()
      .toLowerCase();
    const config = labelToConfig.get(labelKey);
    if (!config) continue;
    // Avoid double-matching the same section: if we already have this
    // config, keep only the FIRST occurrence (later headers with the
    // same label become part of the previous section's body).
    if (matches.some(x => x.config.id === config.id)) continue;
    const lineStart = m.index;
    // Find the end of the header line so the body starts on the next line.
    const newlineAfter = fullMainText.indexOf('\n', m.index + m[0].length);
    const bodyStart = newlineAfter === -1 ? fullMainText.length : newlineAfter + 1;
    matches.push({ index: lineStart, bodyStart, config });
  }
  matches.sort((a, b) => a.index - b.index);

  let fullMainBody = fullMainText;
  let sectionsData = {};

  if (matches.length > 0) {
    fullMainBody = fullMainText.substring(0, matches[0].index).trim();
    for (let i = 0; i < matches.length; i++) {
      const current = matches[i];
      const next = matches[i + 1];
      const start = current.bodyStart;
      const end = next ? next.index : fullMainText.length;
      sectionsData[current.config.id] = fullMainText.substring(start, end).trim();
    }
  }

  if (!cleanContent) {
    return (
        <div className="message-content text-base leading-relaxed space-y-1.5 text-foreground min-h-[24px] flex items-center">
            <TypingIndicator />
        </div>
    )
  }

  return (
    <div className="message-content text-base leading-relaxed space-y-1.5 text-foreground">
      {fullImageMatch && (
        <div className="mb-3 animate-in fade-in zoom-in-95 duration-500">
          <img 
            src={fullImageMatch[2]} 
            alt={t('generated.messagedisplay_video_thumbnail_2d30')} 
            className="w-full h-auto rounded-2xl border border-border shadow-sm object-cover"
            style={{ maxHeight: '300px' }}
          />
        </div>
      )}
      <div>
        <TypewriterText text={fullMainBody} onScriptureClick={onScriptureClick} />
      </div>
      {sectionsConfig.map(config => {
        const sectionContent = sectionsData[config.id];
        if (!sectionContent) return null;
        
        // Scripture References renders as a row of tappable chips that
        // open the unified ScripturePopup (parity with Daily Reflections).
        if (config.renderAsChips) {
          return (
            <CollapsibleSection
              key={config.id}
              title={t(config.translationKey, config.fallbackLabel || config.header.replace('### ', ''))}
              content={sectionContent}
              icon={config.icon}
              defaultOpen
              renderContent={(c) => (
                <ScriptureRefChips text={c} onScriptureClick={onScriptureClick} />
              )}
            />
          );
        }

        return (
          <CollapsibleSection 
            key={config.id}
            title={t(config.translationKey, config.fallbackLabel || config.header.replace('### ', ''))} 
            content={sectionContent} 
            icon={config.icon}
            renderContent={(t) => <TypewriterText text={t} speed={5} onScriptureClick={onScriptureClick} />} 
          />
        );
      })}
    </div>
  );
}

// Render text directly — real-time streaming from backend provides natural progressive display
function TypewriterText({ text, speed = 10, onScriptureClick }) {
    return <MarkdownRenderer text={text || ''} onScriptureClick={onScriptureClick} />;
}

// Extracted Markdown Logic with Theme Classes
function MarkdownRenderer({ text, onScriptureClick }) {
  const {
    t
  } = useTranslation();

  if (!text) return null;

  // ── File-attachment chip: the backend prepends
  // `<!--FILE_CHIP:filename-->` to the assistant message it creates on
  // a successful /api/upload so users always see which document Eili
  // has in context for the conversation. We strip the marker from the
  // rendered body and render a small pill above the text.
  let fileChipName = null;
  const fileChipMatch = text.match(/^<!--FILE_CHIP:(.+?)-->\s*/);
  if (fileChipMatch) {
    fileChipName = fileChipMatch[1];
    text = text.slice(fileChipMatch[0].length);
  }

  const bibleBooks = '(?:Genesis|Exodus|Leviticus|Numbers|Deuteronomy|Joshua|Judges|Ruth|1\\s?Samuel|2\\s?Samuel|1\\s?Kings|2\\s?Kings|1\\s?Chronicles|2\\s?Chronicles|Ezra|Nehemiah|Esther|Job|Psalms?|Proverbs|Ecclesiastes|Song\\s?of\\s?Solomon|Isaiah|Jeremiah|Lamentations|Ezekiel|Daniel|Hosea|Joel|Amos|Obadiah|Jonah|Micah|Nahum|Habakkuk|Zephaniah|Haggai|Zechariah|Malachi|Matthew|Mark|Luke|John|Acts|Romans|1\\s?Corinthians|2\\s?Corinthians|Galatians|Ephesians|Philippians|Colossians|1\\s?Thessalonians|2\\s?Thessalonians|1\\s?Timothy|2\\s?Timothy|Titus|Philemon|Hebrews|James|1\\s?Peter|2\\s?Peter|1\\s?John|2\\s?John|3\\s?John|Jude|Revelation|Génesis|Éxodo|Levítico|Números|Deuteronomio|Josué|Jueces|Rut|Salmos?|Proverbios|Eclesiastés|Cantares|Isaías|Jeremías|Lamentaciones|Ezequiel|Oseas|Amós|Abdías|Jonás|Miqueas|Nahúm|Habacuc|Sofonías|Hageo|Zacarías|Malaquías|Mateo|Marcos|Lucas|Juan|Hechos|Romanos|Corintios|Gálatas|Efesios|Filipenses|Colosenses|Tesalonicenses|Timoteo|Filemón|Hebreos|Santiago|Pedro|Judas|Apocalipsis)';
  const scriptureRegex = new RegExp(`(${bibleBooks}\\s\\d+:\\d+(?:[\\-\\u2013,]\\d+)*)`, 'gi');
  const refRe = `${bibleBooks}\\s\\d+:\\d+`;
  // Reference right after the quote: `"…" (John 8:12)` / `"…" — John 8:12`
  const refAfter = new RegExp(`^[\\s,]*[(\\[\\u2014\\u2013-]*\\s*${refRe}`, 'i');
  // Reference just before it, same clause: `John 8:12 says, "…"`
  const refBefore = new RegExp(`${refRe}(?:[\\-\\u2013,]\\d+)*\\)?[^"\\u201C\\u201D.!?]{0,30}$`, 'i');

  // Private-use sentinels mark Bible-verse quotations. They are consumed as
  // tokens by renderQuoted() and are never rendered (no tofu boxes).
  const SQ_OPEN = '\uE010';
  const SQ_CLOSE = '\uE011';
  const SQ_SPLIT = /([\uE010\uE011])/;

  // Pair quote marks left-to-right (so short words like "souling" pair
  // correctly) and italicize only real verse quotes: 8+ chars with a
  // scripture reference directly before or after the quote.
  const markVerseQuotes = (line) => line.replace(
    /"([^"\n]+)"|\u201C([^\u201D\n]+)\u201D/g,
    (match, straight, curly, offset, str) => {
      const inner = straight ?? curly;
      if (inner.length < 8) return match;
      const before = str.slice(Math.max(0, offset - 60), offset);
      const after = str.slice(offset + match.length, offset + match.length + 40);
      return refBefore.test(before) || refAfter.test(after)
        ? `${SQ_OPEN}${match}${SQ_CLOSE}` : match;
    },
  );

  let cleanedText = text
    // Heading prefixes -> bold (any depth, optional space)
    .replace(/^#{1,6}\s+(.+)$/gm, '**$1**')
    // Triple asterisks -> double
    .replace(/\*{3,}([^*\n]+?)\*{3,}/g, '**$1**')
    // List markers at line start -> bullet
    .replace(/^([ \t]*)[*\-–][ \t]+/gm, '$1• ')
    // Stray standalone ** on its own line
    .replace(/^\s*\*\*\s*$/gm, '')
    // Drop any sentinel-range chars from the source, then mark verse quotes
    .replace(/[\uE010\uE011]/g, '')
    .split('\n').map(markVerseQuotes).join('\n');

  // Render a fragment, italicizing text between sentinels. `q` carries the
  // open/closed state across fragments of one line (bold, links, etc.).
  const renderQuoted = (fragment, keyPrefix, q) => {
    if (typeof fragment !== 'string' || !SQ_SPLIT.test(fragment)) {
      return q.open && fragment
        ? <em key={keyPrefix} className="italic text-muted-foreground" data-testid="scripture-quote">{fragment}</em>
        : fragment;
    }
    return fragment.split(SQ_SPLIT).map((piece, k) => {
      if (piece === SQ_OPEN) { q.open = true; return null; }
      if (piece === SQ_CLOSE) { q.open = false; return null; }
      if (!piece) return null;
      return q.open
        ? <em key={`${keyPrefix}-sq-${k}`} className="italic text-muted-foreground" data-testid="scripture-quote">{piece}</em>
        : <React.Fragment key={`${keyPrefix}-t-${k}`}>{piece}</React.Fragment>;
    });
  };

  // Eili's own *italics*: blue accent for sources/citations, normal colour
  // for plain emphasis (foreign terms, stressed words).
  const CITATION_RE = /^(source|see|cf\.|according to)\b|\b(1[5-9]\d{2}|20\d{2})\b|\b(press|ed\.|eds\.|vol\.|pp?\.|journal|commentary|university)\b/i;

  // Regex for scripture references: Book Chapter:Verse(-Verse)?
  // STRICT: Only match actual Bible book names, not arbitrary capitalized words
  // Matches: "Genesis 4:7", "John 1:9", "1 John 3:16", "Song of Solomon 2:4"
  // Does NOT match: "Cain in Genesis 4:7" (Cain is not a book)

  const sections = cleanedText.split(/\n\s*_{3,}\s*\n/);
  const renderedSections = sections.map((section, idx) => {
    const paragraphs = section.split('\n');
    const renderedParagraphs = paragraphs.map((line, pIdx) => {
      const q = { open: false };
      // Simple bold/italic parser - consistent with previous logic but using theme classes
      const boldParts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <div key={pIdx} className="min-h-[1.5em] text-foreground">
          {boldParts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              // 🐛 FIX (Aug 2026): raw PUA sentinels used to leak
              // inside <strong> content as tofu rectangles on Android
              // system fonts. Route the interior through the italic
              // splitter too so the quoted-scripture sentinels get
              // converted into <em> nodes just like non-bold text.
              return (
                <strong key={i} className="font-bold text-foreground">
                  {renderQuoted(part.slice(2, -2), `${pIdx}-${i}-strong`, q)}
                </strong>
              );
            }
            const italicParts = part.split(/(\*[^*]+?\*)/g);
            return (
              <span key={i}>
                {italicParts.map((subPart, j) => {
                  if (subPart.startsWith('*') && subPart.endsWith('*') && subPart.length > 2) {
                    // Italics are reserved for citations / references / attributions
                    // (e.g., commentator names, scholarly works, historical sources).
                    // Use a distinct accent tint so they stand out from body text.
                    const inner = subPart.slice(1, -1);
                    const isCitation = CITATION_RE.test(inner.replace(/[\uE010\uE011]/g, ''));
                    return (
                      <em
                        key={j}
                        className={isCitation ? 'italic font-medium' : 'italic'}
                        style={isCitation ? { color: 'var(--theme-accent)' } : undefined}
                        data-testid={isCitation ? 'citation-italic' : 'emphasis-italic'}
                      >
                        {renderQuoted(inner, `${pIdx}-${i}-${j}-em`, q)}
                      </em>
                    );
                  }
                  
                  // Scripture Link Parsing
                  if (onScriptureClick) {
                      const scriptureParts = subPart.split(scriptureRegex);
                      return (
                        <span key={j}>
                          {scriptureParts.map((sp, k) => {
                              if (sp.match(scriptureRegex)) {
                                  return (
                                    <button 
                                        key={k} 
                                        onClick={() => onScriptureClick(sp)}
                                        className="text-primary hover:underline font-medium bg-primary/10 px-1 rounded cursor-pointer"
                                        title={t('generated.messagedisplay_open_in_bible_c08f')}
                                    >
                                      {sp}
                                    </button>
                                  );
                              }
                              return (
                                <React.Fragment key={k}>
                                  {renderQuoted(sp, `${pIdx}-${i}-${j}-${k}`, q)}
                                </React.Fragment>
                              );
                          })}
                        </span>
                      );
                  }

                  return (
                    <React.Fragment key={j}>
                      {renderQuoted(subPart, `${pIdx}-${i}-${j}`, q)}
                    </React.Fragment>
                  );
                })}
              </span>
            );
          })}
        </div>
      );
    });
    return (
      <React.Fragment key={idx}>
        {idx > 0 && <hr className="my-4 border-t border-border" />}
        <div className="space-y-1">{renderedParagraphs}</div>
      </React.Fragment>
    );
  });

  return (
    <>
      {fileChipName && (
        <div
          className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary max-w-full"
          data-testid="file-chip"
          title={`Attached to this conversation: ${fileChipName}`}
        >
          <Paperclip className="h-3 w-3 shrink-0" />
          <span className="truncate">{fileChipName}</span>
        </div>
      )}
      {renderedSections}
    </>
  );
}
function TypingIndicator() {
  return (
    <div className="flex gap-1.5 items-center h-6 pl-1">
      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.3s]" />
      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.15s]" />
      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" />
    </div>
  );
}

/**
 * ScriptureRefChips — Render the appendix as a row of tappable chips,
 * matching the Daily Reflections UX. Each chip opens the unified
 * ScripturePopup on tap (via the parent's onScriptureClick handler).
 *
 * Accepts ANY of these line formats so it tolerates both the new
 * (references-only) appendix format AND legacy messages stored in DB
 * with the old "Ref: \"text\"" format:
 *
 *   - John 3:16
 *   - John 3:16 (ESV): "For God so loved..."
 *   - John 3:16: For God so loved...
 *
 * Only the reference portion is rendered; any trailing verse text from
 * legacy entries is silently dropped (the popup fetches canonical text
 * on demand instead).
 */
function ScriptureRefChips({ text, onScriptureClick }) {
  const {
    t
  } = useTranslation();

  if (!text) return null;

  // Bible book whitelist (English + common localisations) so a stray
  // capitalised word in a legacy line doesn't become a chip.
  const BIBLE_BOOKS_RE = /(?:Genesis|Exodus|Leviticus|Numbers|Deuteronomy|Joshua|Judges|Ruth|1\s?Samuel|2\s?Samuel|1\s?Kings|2\s?Kings|1\s?Chronicles|2\s?Chronicles|Ezra|Nehemiah|Esther|Job|Psalms?|Proverbs|Ecclesiastes|Song\s?of\s?Solomon|Song\s?of\s?Songs|Isaiah|Jeremiah|Lamentations|Ezekiel|Daniel|Hosea|Joel|Amos|Obadiah|Jonah|Micah|Nahum|Habakkuk|Zephaniah|Haggai|Zechariah|Malachi|Matthew|Mark|Luke|John|Acts|Romans|1\s?Corinthians|2\s?Corinthians|Galatians|Ephesians|Philippians|Colossians|1\s?Thessalonians|2\s?Thessalonians|1\s?Timothy|2\s?Timothy|Titus|Philemon|Hebrews|James|1\s?Peter|2\s?Peter|1\s?John|2\s?John|3\s?John|Jude|Revelation)/i;

  // Match a reference at the start of a line (after optional bullet).
  const REF_LINE_RE = new RegExp(
    `^[\\s\\-*•]*(${BIBLE_BOOKS_RE.source}\\s+\\d+(?::\\d+(?:[\\-\\u2013]\\d+)?)?)`,
    'i'
  );

  const refs = [];
  const seen = new Set();
  text.split(/\n/).forEach((line) => {
    const m = line.match(REF_LINE_RE);
    if (m) {
      const ref = m[1].replace(/\s+/g, ' ').trim();
      if (!seen.has(ref.toLowerCase())) {
        seen.add(ref.toLowerCase());
        refs.push(ref);
      }
    }
  });

  if (refs.length === 0) {
    // No parseable refs — fall back to plain text so we don't hide content.
    return <div className="text-sm whitespace-pre-line">{text}</div>;
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid="chat-scripture-ref-chips">
      {refs.map((ref, i) => (
        <button
          key={`${ref}-${i}`}
          onClick={() => onScriptureClick?.(ref)}
          className="text-sm px-3 py-1.5 rounded-lg transition-all hover:scale-105 active:scale-95 cursor-pointer"
          style={{
            background: 'var(--theme-hover)',
            color: 'var(--theme-accent)',
            border: '1px solid var(--theme-border)',
          }}
          data-testid={`chat-scripture-ref-${i}`}
          title={t('generated.messagedisplay_tap_to_read_80aa')}
        >
          {ref}
        </button>
      ))}
    </div>
  );
}

function CollapsibleSection({ title, content, icon, renderContent, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mt-2 border border-border rounded-lg overflow-hidden bg-muted/30">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-muted hover:bg-muted/80 transition-colors text-left"
        data-testid={`toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-semibold text-foreground text-sm">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      
      {isOpen && (
        <div className="p-4 bg-card border-t border-border text-sm text-card-foreground leading-relaxed animate-in slide-in-from-top-2 duration-200">
          {renderContent ? renderContent(content) : content}
        </div>
      )}
    </div>
  );
}
